// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedChargeDeltaProperty } from '@/audit/api/types';
import {
  ChargeStatus,
  ChargeStatusLabelMap,
  ChargeStatuses,
} from '@/constants/charge-status';
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useState } from 'react';

interface Props {
  property: CachedChargeDeltaProperty;
}

interface ChargeItem {
  label: string;
  totalPrice: string;
  quantity: number;
  unitPrice: string | null;
  status: string;
  pricingMode: string;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  expectedBillableDate: string | null;
  billingEligibleDate: string | null;
}

function formatChargeSummary(charge: ChargeItem): string {
  return `${charge.label} — ${charge.totalPrice} (qty: ${String(charge.quantity)})`;
}

interface FieldChange {
  field: string;
  oldValue: string;
  newValue: string;
}

const NONE = 'None';

// UnitPrice / totalPrice already arrive as formatted currency strings from
// auditCurrencyValueSchema, so these pass the value through (null unit price
// reads "None").
function formatTotal(value: string): string {
  return value;
}

function formatUnitPrice(value: string | null): string {
  return value ?? NONE;
}

// The audit payload stores the raw ChargeStatus enum string. Map it to
// the user-facing label the rest of the FE uses, falling back to the raw
// value if the audit carries an unknown status (e.g. a legacy enum
// member that's since been retired).
function formatStatus(value: string): string {
  if ((ChargeStatuses as string[]).includes(value)) {
    return ChargeStatusLabelMap(value as ChargeStatus);
  }
  return value;
}

// Builds the ordered field list used for both update diffs (old/new pairs)
// and single-sided add/remove rendering (one side formatted as "none").
function chargeFields(charge: ChargeItem): FieldChange[] {
  return [
    { field: 'Label', oldValue: charge.label, newValue: charge.label },
    {
      field: 'Total',
      oldValue: formatTotal(charge.totalPrice),
      newValue: formatTotal(charge.totalPrice),
    },
    {
      field: 'Quantity',
      oldValue: String(charge.quantity),
      newValue: String(charge.quantity),
    },
    {
      field: 'Unit price',
      oldValue: formatUnitPrice(charge.unitPrice),
      newValue: formatUnitPrice(charge.unitPrice),
    },
    {
      field: 'Status',
      oldValue: formatStatus(charge.status),
      newValue: formatStatus(charge.status),
    },
    {
      field: 'Period start',
      oldValue: charge.servicePeriodStart ?? NONE,
      newValue: charge.servicePeriodStart ?? NONE,
    },
    {
      field: 'Period end',
      oldValue: charge.servicePeriodEnd ?? NONE,
      newValue: charge.servicePeriodEnd ?? NONE,
    },
    {
      field: 'Expected bill date',
      oldValue: charge.expectedBillableDate ?? NONE,
      newValue: charge.expectedBillableDate ?? NONE,
    },
    {
      field: 'Eligible bill date',
      oldValue: charge.billingEligibleDate ?? NONE,
      newValue: charge.billingEligibleDate ?? NONE,
    },
  ];
}

// Drops field rows where the two sides are identical — those reads as
// "X → X" with no information content. Applies to every diff type:
// added rows whose field was null in the new value, removed rows whose
// field was already null, and (defensively) any other path where a
// change row ends up with equal sides.
function omitUnchanged(changes: FieldChange[]): FieldChange[] {
  return changes.filter((change) => change.oldValue !== change.newValue);
}

// Added: every field transitions from "none" to the new value. Fields
// whose new value is also "none" (e.g. an unset billing-eligible date
// on a fresh PLANNED charge) are suppressed — "none → none" is noise.
function addedCharges(charge: ChargeItem): FieldChange[] {
  return omitUnchanged(
    chargeFields(charge).map((change) => ({
      field: change.field,
      oldValue: NONE,
      newValue: change.newValue,
    }))
  );
}

// Removed: every field transitions from its prior value to "none". Same
// noise filter as addedCharges — drop rows whose prior value was "none"
// (never-set fields that stayed never-set).
function removedCharges(charge: ChargeItem): FieldChange[] {
  return omitUnchanged(
    chargeFields(charge).map((change) => ({
      field: change.field,
      oldValue: change.oldValue,
      newValue: NONE,
    }))
  );
}

// Updated: only fields that actually differ between old and new.
function updatedCharges(
  oldCharge: ChargeItem,
  newCharge: ChargeItem
): FieldChange[] {
  const oldFields = chargeFields(oldCharge);
  const newFields = chargeFields(newCharge);
  const changes: FieldChange[] = [];
  for (let i = 0; i < oldFields.length; i++) {
    if (oldFields[i].newValue !== newFields[i].newValue) {
      changes.push({
        field: oldFields[i].field,
        oldValue: oldFields[i].newValue,
        newValue: newFields[i].newValue,
      });
    }
  }
  return changes;
}

// Styles a single old/new value in the expanded diff. A cleared field
// renders as "none" in text.secondary so it visually reads as absent
// data, distinct from a real string next to it in the transition.
function DeltaValue(props: { value: string }) {
  const isNone = props.value === NONE;
  return (
    <Box component="span" sx={{ color: isNone ? 'text.secondary' : 'inherit' }}>
      {props.value}
    </Box>
  );
}

// Only the update branch shows a "old → new" transition. Added / removed
// rows display a single value because the other side is always "none" by
// definition, and rendering it adds noise.
type ExpandableChargeRowKind = 'added' | 'removed' | 'updated';

interface ExpandableChargeRowProps {
  summary: string;
  changes: FieldChange[];
  kind: ExpandableChargeRowKind;
}

function renderChangeLine(
  change: FieldChange,
  kind: ExpandableChargeRowKind
): JSX.Element {
  switch (kind) {
    case 'added':
      return (
        <>
          {change.field}: <DeltaValue value={change.newValue} />
        </>
      );
    case 'removed':
      return (
        <>
          {change.field}: <DeltaValue value={change.oldValue} />
        </>
      );
    case 'updated':
      return (
        <>
          {change.field}: <DeltaValue value={change.oldValue} /> →{' '}
          <DeltaValue value={change.newValue} />
        </>
      );
  }
}

function ExpandableChargeRow(props: ExpandableChargeRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetail = props.changes.length > 0;

  if (!hasDetail) {
    return (
      <Typography variant="caption" sx={{ marginLeft: 2 }}>
        {props.summary}
      </Typography>
    );
  }

  return (
    <Stack sx={{ marginLeft: 2 }}>
      <ButtonBase
        onClick={() => setIsExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreRounded
          color="action"
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms',
          }}
        />
        <Typography variant="caption" fontWeight={500}>
          {props.summary}
        </Typography>
      </ButtonBase>
      {isExpanded && (
        <Box sx={{ pl: 3 }}>
          {props.changes.map((change) => (
            <Typography key={change.field} variant="caption" display="block">
              {renderChangeLine(change, props.kind)}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
}

export function AuditCachedChargeDeltaLabel(props: Props) {
  const added = props.property.newValue?.added ?? [];
  const removed = props.property.newValue?.removed ?? [];
  const updated = props.property.newValue?.updated ?? [];

  if (added.length === 0 && removed.length === 0 && updated.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>

      {added.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Added:
          </Typography>
          {added.map((item, i) => (
            <ExpandableChargeRow
              key={i}
              summary={formatChargeSummary(item)}
              changes={addedCharges(item)}
              kind="added"
            />
          ))}
        </Stack>
      )}

      {removed.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Removed:
          </Typography>
          {removed.map((item, i) => (
            <ExpandableChargeRow
              key={i}
              summary={formatChargeSummary(item)}
              changes={removedCharges(item)}
              kind="removed"
            />
          ))}
        </Stack>
      )}

      {updated.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Updated:
          </Typography>
          {updated.map((updateItem, i) => (
            <ExpandableChargeRow
              key={i}
              summary={formatChargeSummary(updateItem.new)}
              changes={updatedCharges(updateItem.old, updateItem.new)}
              kind="updated"
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
